import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography, IconButton } from '@material-ui/core';
import { getDateBefore, getDateAfter, isSameToday, isSameDate } from 'utils/timing';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import useMediaQueries from 'hooks/useMediaQueries';
import { ArrowRight, ArrowLeft } from '@material-ui/icons';
import { useIsFetchingBookings, useSelectedDate } from 'hooks/calendar';
import * as calendarActions from 'views/calender/actions';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles((theme) => ({
    root: {
        height: 50,
        borderRadius: 40,
        background: theme.palette.common.white,
        // boxShadow: theme.shadows[1],
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        '& *': {
            pointerEvents: (props) => (props.disabled ? 'none' : '')
        }
    },
    today: {
        width: 60,
        background: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',
        fontSize: 14,
        fontWeight: 500,
        justifyContent: 'center',
        cursor: 'pointer',
        borderRadius: 20,
        padding: `4px 20px`
    },
    todaySelected: {
        background: theme.palette.primary.main
    },
    todayText: {
        color: theme.palette.common.white
    },
    datePicker: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minWidth: 180,
        cursor: 'pointer'
    },
    picker: {
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        margin: 'auto',
        '& .MuiTextField-root': {
            opacity: 0,
            width: '100%',
            cursor: 'pointer !important'
        },
        '& input': {
            cursor: 'pointer'
        }
    }
}));

function DateRange() {
    const classes = useStyles();
    const { isMobile } = useMediaQueries();
    const { t } = useTranslation();
    const selectedDate = useSelectedDate();
    const dispatch = useDispatch();
    const disabled = useIsFetchingBookings();

    const handleDateChange = React.useCallback(
        (date) => {
            dispatch(calendarActions.setSelectedDate(date));
        },
        [dispatch]
    );

    const handleChange = React.useCallback(
        (date) => {
            if (isSameDate({ date1: date, date2: selectedDate })) {
                return;
            }
            handleDateChange(date);
        },
        [handleDateChange, selectedDate]
    );

    const handleTodayClick = React.useCallback(() => {
        if (isSameToday(selectedDate)) {
            return;
        }
        handleDateChange(moment());
    }, [handleDateChange, selectedDate]);

    const handleNextClick = React.useCallback(() => {
        const nextDate = getDateAfter({ date: selectedDate, numOfDay: 1 });
        const date = moment(nextDate);

        handleDateChange(date);
    }, [handleDateChange, selectedDate]);

    const handlePrevClick = React.useCallback(() => {
        const prevDate = getDateBefore({ date: selectedDate, numOfDay: 1 });
        const date = moment(prevDate);
        handleDateChange(date);
    }, [handleDateChange, selectedDate]);

    const DateRangeText = React.useMemo(() => {
        return moment(selectedDate).format(`dddd DD MMM, YYYY`);
    }, [selectedDate]);

    return (
        <Grid
            container
            style={{ pointerEvents: disabled ? 'none' : '' }}
            className={classes.root}
            direction="row"
            wrap="nowrap"
            alignItems="center"
        >
            <Grid item className={`${classes.center} ${classes.arrow}`}>
                <IconButton onClick={handlePrevClick} size="small">
                    <ArrowLeft />
                </IconButton>
            </Grid>
            <Grid
                item
                className={`${classes.center} ${classes.today} ${
                    isSameToday(selectedDate) ? classes.todaySelected : ''
                }`}
                onClick={handleTodayClick}
            >
                <Typography className={isSameToday(selectedDate) ? classes.todayText : ''} variant="body1">
                    {t(`today`)}
                </Typography>
            </Grid>
            <Grid
                item
                style={{ pointerEvents: disabled ? 'none' : '' }}
                className={`${classes.center} ${classes.datePicker}`}
            >
                <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                    <div className={classes.picker}>
                        <DatePicker
                            variant={isMobile ? 'dialog' : 'inline'}
                            label="Basic example"
                            value={selectedDate}
                            onChange={handleChange}
                            disableToolbar
                            format="DD/MM/YYYY"
                            autoOk={true}
                        />
                    </div>
                </MuiPickersUtilsProvider>
                <Typography variant="body1" className={classes.present}>
                    {DateRangeText}
                </Typography>
            </Grid>
            <Grid item className={`${classes.center} ${classes.arrow}`} onClick={handleNextClick}>
                <IconButton onClick={handleNextClick} size="small">
                    <ArrowRight />
                </IconButton>
            </Grid>
        </Grid>
    );
}

export default React.memo(DateRange);
